import { useRef, useState } from 'react';
import { FlatList, Pressable } from 'react-native';
import { type SharedValue } from 'react-native-reanimated';
import {
    AnimatePresence,
    Animations,
    BodySmallRegular,
    isNonMobileWeb,
    tokens,
    View,
    YStack,
} from 'smoothie';

import { useAuthedUser } from '../../../common/providers/AuthedUserProvider';
import { getNonTerminatedCardsForUser } from '../../../common/utils/cardUtils';
import { type Card, CARD_ASPECT_RATIO } from '../../types/Card';
import CardDetails from './card-details/CardDetails';
import { CardFlip } from './CardFlip';
import { FruitfulCardSummary } from './FruitfulCard';

const MAX_CARD_WIDTH = 355;
const PEEK = 12;
const GAP = 12;
const SIDE_PADDING = PEEK + GAP;

interface CardCarouselProps {
    syncteraAccountId: string;
    cards: Card[];
    onIndexChange?: (index: number) => void;
    isFlipped?: SharedValue<boolean>;
    showDetailsHint?: boolean;
}

export const CardCarousel = ({
    syncteraAccountId: _syncteraAccountId,
    cards,
    onIndexChange,
    isFlipped,
    showDetailsHint,
}: CardCarouselProps) => {
    const user = useAuthedUser();
    const filteredCards = getNonTerminatedCardsForUser(
        user.syncteraPersonId,
        cards,
    );
    const [containerWidth, setContainerWidth] = useState(0);
    const [currentIndex, setCurrentIndex] = useState(0);
    const flatListRef = useRef<FlatList>(null);

    if (filteredCards.length === 0) return null;

    const cardWidth = Math.min(
        containerWidth - 2 * SIDE_PADDING,
        MAX_CARD_WIDTH,
    );
    const snapInterval = cardWidth + GAP;
    const showDots = filteredCards.length > 1;

    const handleIndexChange = (index: number) => {
        setCurrentIndex(index);
        onIndexChange?.(index);
    };

    const handleScrollEnd = (e: {
        nativeEvent: { contentOffset: { x: number } };
    }) => {
        const index = Math.round(e.nativeEvent.contentOffset.x / snapInterval);
        handleIndexChange(
            Math.max(0, Math.min(index, filteredCards.length - 1)),
        );
    };

    return (
        <YStack
            width="100%"
            onLayout={(e) => {
                setContainerWidth(e.nativeEvent.layout.width);
            }}
            gap={16}
            backgroundColor={isNonMobileWeb ? '#fafafa' : 'transparent'}
            paddingVertical={isNonMobileWeb ? 16 : 0}
            borderRadius={24}
        >
            {containerWidth > 0 && (
                <FlatList
                    ref={flatListRef}
                    horizontal
                    data={filteredCards}
                    keyExtractor={(item) => item.id}
                    showsHorizontalScrollIndicator={false}
                    decelerationRate="fast"
                    snapToOffsets={filteredCards.map(
                        (_, i) => i * snapInterval,
                    )}
                    contentContainerStyle={{
                        paddingHorizontal: SIDE_PADDING,
                        marginHorizontal: 'auto',
                    }}
                    ItemSeparatorComponent={() => (
                        <View style={{ width: GAP }} />
                    )}
                    onMomentumScrollEnd={handleScrollEnd}
                    onScrollEndDrag={handleScrollEnd}
                    renderItem={({ item, index }) => {
                        const isActive = index === currentIndex;
                        const card = (
                            <FruitfulCardSummary
                                width={cardWidth}
                                cardholderName={item.embossName.line1}
                                form={item.form}
                                color={item.color}
                                lastFour={item.lastFour}
                                cardStatus={item.cardStatus}
                                userCardStatusReason={item.userCardStatusReason}
                                padding={16}
                                onPress={() => {
                                    flatListRef.current?.scrollToOffset({
                                        offset: index * snapInterval,
                                        animated: true,
                                    });
                                    handleIndexChange(index);
                                }}
                            />
                        );
                        return (
                            <YStack>
                                <View
                                    height={cardWidth / CARD_ASPECT_RATIO + 12}
                                    justifyContent="center"
                                >
                                    {isActive && isFlipped != null ? (
                                        <CardFlip
                                            isFlipped={isFlipped}
                                            front={card}
                                            back={
                                                <CardDetails
                                                    cardId={item.id}
                                                    width={cardWidth}
                                                />
                                            }
                                        />
                                    ) : (
                                        card
                                    )}
                                </View>

                                <AnimatePresence>
                                    {showDetailsHint && isActive && (
                                        <YStack
                                            alignSelf="center"
                                            width={cardWidth}
                                            {...Animations.FadeInFromAbove}
                                            exitStyle={{
                                                opacity: 0,
                                                transform: [
                                                    { translateY: -20 },
                                                ],
                                            }}
                                            animateOnly={[
                                                'opacity',
                                                'transform',
                                            ]}
                                        >
                                            <View
                                                marginLeft="10%"
                                                alignSelf="flex-start"
                                                borderColor="transparent"
                                                borderBottomColor="$gray1200"
                                                borderWidth={10}
                                                borderTopWidth={0}
                                                zIndex={1}
                                            />
                                            <YStack
                                                backgroundColor="$gray1200"
                                                borderRadius={12}
                                                padding={12}
                                            >
                                                <BodySmallRegular
                                                    fontWeight={600}
                                                    color="$white"
                                                >
                                                    The Fruitful Card is not a
                                                    debit card.
                                                </BodySmallRegular>
                                                <BodySmallRegular color="$white">
                                                    When linking to services
                                                    like Venmo or PayPal, make
                                                    sure to use your Cash
                                                    account number instead.
                                                </BodySmallRegular>
                                            </YStack>
                                        </YStack>
                                    )}
                                </AnimatePresence>
                            </YStack>
                        );
                    }}
                />
            )}
            {showDots && (
                <View
                    flexDirection="row"
                    gap={8}
                    alignItems="center"
                    justifyContent="center"
                >
                    {filteredCards.map((_, index) => (
                        <Pressable
                            key={index}
                            onPress={() => {
                                flatListRef.current?.scrollToOffset({
                                    offset: index * snapInterval,
                                    animated: true,
                                });
                                handleIndexChange(index);
                            }}
                            style={{
                                width: 8,
                                height: 8,
                                borderRadius: 4,
                                backgroundColor:
                                    index === currentIndex
                                        ? tokens.color.light.green.green900
                                        : tokens.color.light.gray.gray400,
                            }}
                        />
                    ))}
                </View>
            )}
        </YStack>
    );
};
